import { type Dayjs } from 'dayjs';
import { type ReactElement, useMemo, useState } from 'react';
import { EntityDailyHistoryPanel } from '@/features/shared/components/entityDailyHistory/EntityDailyHistoryPanel';
import { resolveWindowDayKeys } from '@/features/shared/utils/chartDateWindow';
import { useSuiteHistoryPanelData } from '@/features/suites/pages/useSuiteHistoryPanelData';
import { getChartTitle } from '@/shared/copy/charts';
import type { SuiteRunStatus } from '@flowqa/shared';

type SuiteHistoryDateWindow = {
  startDay: string | null;
  endDay: string | null;
  previousStartDay: string | null;
  previousEndDay: string | null;
};

type SuitesPageHistorySectionProps = {
  panelDateQuickFilter: string;
  panelDateWindow: SuiteHistoryDateWindow;
  panelFilterStatuses: SuiteRunStatus[];
  panelFilterSuites: string[];
  panelFilterTags: string[];
  panelNoRowsText: string;
  panelSearchValue: string;
  latestDateBounds: { from: Dayjs | null; to: Dayjs | null };
  selectedOrganisationId: string | null;
  selectedProjectId: string | null;
};

export const SuitesPageHistorySection = ({
  panelDateQuickFilter,
  panelDateWindow,
  panelFilterStatuses,
  panelFilterSuites,
  panelFilterTags,
  panelNoRowsText,
  panelSearchValue,
  latestDateBounds,
  selectedOrganisationId,
  selectedProjectId,
}: SuitesPageHistorySectionProps): ReactElement => {
  const [expandedRowIds, setExpandedRowIds] = useState<Set<string>>(() => new Set());
  const expandedSuiteIds = useMemo(() => [...expandedRowIds], [expandedRowIds]);
  const historyDayKeys = useMemo(
    () =>
      resolveWindowDayKeys({
        startDay: panelDateWindow.startDay,
        endDay: panelDateWindow.endDay,
        fallbackStartDay: latestDateBounds.from?.format('YYYY-MM-DD') ?? null,
        fallbackEndDay: latestDateBounds.to?.format('YYYY-MM-DD') ?? null,
        descending: true,
      }),
    [panelDateWindow.endDay, panelDateWindow.startDay, latestDateBounds.from, latestDateBounds.to],
  );
  const {
    dailyHistoryLoading,
    dailyHistoryRows,
    historyPage,
    historyPageSize,
    historyLatestDateBounds,
    historySortDirection,
    historySortKey,
    showErrorState,
    setHistoryPage,
    setHistoryPageSize,
    setHistorySortDirection,
    setHistorySortKey,
    totalCount,
  } = useSuiteHistoryPanelData({
    panelDateQuickFilter,
    panelDateWindow,
    panelSearchValue,
    panelFilterStatuses,
    panelFilterSuites,
    panelFilterTags,
    expandedSuiteIds,
    latestDateBounds: {
      startDay: latestDateBounds.from?.format('YYYY-MM-DD') ?? null,
      endDay: latestDateBounds.to?.format('YYYY-MM-DD') ?? null,
    },
    selectedOrganisationId,
    selectedProjectId,
  });

  const resolvedDateRangeLabel = useMemo(() => {
    const fromDate = historyLatestDateBounds.from;
    const toDate = historyLatestDateBounds.to;
    if (fromDate && toDate) {
      return `${fromDate.format('MMM D, YYYY')} - ${toDate.format('MMM D, YYYY')}`;
    }
    if (fromDate) {
      return `From ${fromDate.format('MMM D, YYYY')}`;
    }
    if (toDate) {
      return `To ${toDate.format('MMM D, YYYY')}`;
    }
    return null;
  }, [historyLatestDateBounds.from, historyLatestDateBounds.to]);

  return (
    <EntityDailyHistoryPanel
      title={getChartTitle('suiteHistory')}
      dateRangeLabel={resolvedDateRangeLabel}
      dayKeys={historyDayKeys}
      entityLabel="Suite"
      activityLabel="runs"
      rows={dailyHistoryRows}
      loading={dailyHistoryLoading}
      showErrorState={showErrorState}
      noRowsText={panelNoRowsText}
      expandedRowIds={expandedRowIds}
      onExpandedRowIdsChange={setExpandedRowIds}
      sortKey={historySortKey}
      sortDirection={historySortDirection}
      sortOptions={[{ key: 'name', label: 'Suite' }]}
      onSortChange={(nextSortKey, nextSortDirection) => {
        setHistorySortKey(nextSortKey);
        setHistorySortDirection(nextSortDirection);
        setHistoryPage(0);
      }}
      page={historyPage}
      pageSize={historyPageSize}
      totalCount={totalCount}
      onPageChange={setHistoryPage}
      onPageSizeChange={(nextPageSize) => {
        setHistoryPageSize(nextPageSize);
        setHistoryPage(0);
      }}
      nameColumnMinWidthPx={180}
      nameColumnDefaultWidthPx={360}
    />
  );
};
